import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import MangaCard from "@/components/base/MangaCard";
import { categories, mangas } from "@/mocks/manga";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState("latest");
  const [search, setSearch] = useState("");

  const currentCategory = categories.find((c) => c.slug === slug);

  const filteredMangas = mangas
    .filter((m) => {
      if (slug && m.category !== slug) return false;
      if (search && !m.title.includes(search) && !m.author.includes(search)) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === "latest") {
        return a.updatedAt > b.updatedAt ? -1 : 1;
      }
      if (sortBy === "popular") {
        return b.views - a.views;
      }
      if (sortBy === "rating") {
        return b.rating - a.rating;
      }
      return 0;
    });

  useEffect(() => {
    document.title = currentCategory
      ? `免费${currentCategory.name} - 樱花漫画网`
      : "漫画分类 - 樱花漫画网";
  }, [currentCategory]);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 bg-background-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              ...(currentCategory ? [{ label: `免费${currentCategory.name}` }] : [{ label: "漫画分类" }]),
            ]}
          />

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
                {currentCategory ? `免费${currentCategory.name}` : "全部漫画"}
              </h1>
              {currentCategory && (
                <p className="text-sm text-foreground-500 mt-1">{currentCategory.description}</p>
              )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  placeholder="搜索漫画..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 pr-4 py-2 w-48 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-all"
                />
              </div>

              <div className="flex items-center bg-background-100 rounded-lg p-1 border border-background-200">
                {[
                  { value: "latest", label: "最新" },
                  { value: "popular", label: "最热" },
                  { value: "rating", label: "评分" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap ${
                      sortBy === opt.value
                        ? "bg-primary-500 text-white"
                        : "text-foreground-600 hover:text-foreground-950"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            <aside className="lg:w-48 flex-shrink-0">
              <div className="bg-background-100 rounded-xl border border-background-200/70 p-4 lg:sticky lg:top-24">
                <h3 className="text-sm font-semibold text-foreground-950 mb-3">漫画分类</h3>
                <div className="space-y-1">
                  <Link
                    to="/category/korean"
                    className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                      slug === "korean"
                        ? "bg-primary-100 text-primary-700 font-medium"
                        : "text-foreground-600 hover:bg-background-200/70 hover:text-foreground-950"
                    }`}
                  >
                    <i className="ri-global-line mr-2 text-xs"></i>
                    免费韩漫
                  </Link>
                  <Link
                    to="/category/japanese"
                    className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                      slug === "japanese"
                        ? "bg-primary-100 text-primary-700 font-medium"
                        : "text-foreground-600 hover:bg-background-200/70 hover:text-foreground-950"
                    }`}
                  >
                    <i className="ri-global-line mr-2 text-xs"></i>
                    免费日漫
                  </Link>
                  <Link
                    to="/category/chinese"
                    className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                      slug === "chinese"
                        ? "bg-primary-100 text-primary-700 font-medium"
                        : "text-foreground-600 hover:bg-background-200/70 hover:text-foreground-950"
                    }`}
                  >
                    <i className="ri-global-line mr-2 text-xs"></i>
                    免费国漫
                  </Link>
                </div>
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {filteredMangas.map((manga) => (
                  <MangaCard key={manga.id} {...manga} />
                ))}
              </div>
              {filteredMangas.length === 0 && (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">没有找到相关漫画</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              name: currentCategory ? `免费${currentCategory.name} - 樱花漫画网` : "漫画分类 - 樱花漫画网",
              description: currentCategory?.description || "樱花漫画网漫画分类页面",
              isPartOf: {
                "@type": "WebSite",
                name: "樱花漫画网",
                url: window.location.origin,
              },
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}